import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Search, X } from "lucide-react";

export interface OpcaoMinisterio {
  id: string;
  nome: string;
}

interface SeletorMinisteriosProps {
  ministerios: OpcaoMinisterio[];
  selecionados: string[];
  onChange: (ids: string[]) => void;
  placeholder?: string;
  vazioAviso?: string;
  id?: string;
}

/**
 * F2-40 (protótipo aprovado 24-25/07/2026): antes disso o campo de
 * ministério era uma lista fixa de checkboxes dentro de uma caixinha com
 * scroll — com mais de ~8 ministérios cadastrados a rolagem ficava
 * incômoda e não dava pra buscar. Este componente troca por busca ao vivo
 * + chips removíveis acima do campo + painel dropdown (mostra todos os
 * ministérios quando a busca está vazia). Reaproveitado em
 * FormularioEvento e FormularioReserva — mesmo contrato de dados de antes
 * (ministerioIds: string[] via onChange), só troca a UI. A lista de
 * opções continua vindo restrita pelo backend (quem não gerencia o
 * módulo só vê os ministérios que lidera) — nada muda nessa regra.
 */
export function SeletorMinisterios({
  ministerios,
  selecionados,
  onChange,
  placeholder = "Buscar ministério...",
  vazioAviso = "Nenhum ministério disponível para vincular.",
  id,
}: SeletorMinisteriosProps) {
  const [busca, setBusca] = useState("");
  const [aberto, setAberto] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function aoClicarFora(e: MouseEvent) {
      if (aberto && wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setAberto(false);
        setBusca("");
      }
    }
    document.addEventListener("mousedown", aoClicarFora);
    return () => document.removeEventListener("mousedown", aoClicarFora);
  }, [aberto]);

  const selecionadosSet = useMemo(() => new Set(selecionados), [selecionados]);

  const listaFiltrada = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return ministerios;
    return ministerios.filter((m) => m.nome.toLowerCase().includes(termo));
  }, [ministerios, busca]);

  const chipsSelecionados = useMemo(
    () => selecionados.map((sid) => ministerios.find((m) => m.id === sid)).filter((m): m is OpcaoMinisterio => Boolean(m)),
    [selecionados, ministerios],
  );

  function alternar(idMinisterio: string) {
    if (selecionadosSet.has(idMinisterio)) {
      onChange(selecionados.filter((sid) => sid !== idMinisterio));
    } else {
      onChange([...selecionados, idMinisterio]);
    }
  }

  function remover(idMinisterio: string) {
    onChange(selecionados.filter((sid) => sid !== idMinisterio));
  }

  function aoTeclar(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") {
      setAberto(false);
      setBusca("");
      (e.target as HTMLInputElement).blur();
    }
  }

  const semOpcoes = ministerios.length === 0;

  return (
    <div className="relative" ref={wrapRef} id={id}>
      {!semOpcoes && chipsSelecionados.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {chipsSelecionados.map((m) => (
            <span
              key={m.id}
              className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 py-1 pl-3 pr-1 text-xs font-semibold text-primary"
            >
              {m.nome}
              <button
                type="button"
                onClick={() => remover(m.id)}
                aria-label={`Remover ${m.nome}`}
                className="grid h-[18px] w-[18px] place-items-center rounded-full hover:bg-primary/20"
              >
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}

      {semOpcoes ? (
        <div className="rounded-md border border-border bg-surface-2 p-2">
          <p className="px-2 py-1 text-xs text-muted">{vazioAviso}</p>
        </div>
      ) : (
        <>
          <div
            onClick={() => setAberto(true)}
            role="combobox"
            aria-expanded={aberto}
            aria-haspopup="listbox"
            className={`flex cursor-text items-center gap-2 rounded-md border bg-surface-2 px-3 py-2.5 transition-colors ${
              aberto ? "border-primary ring-2 ring-primary/20" : "border-border"
            }`}
          >
            <Search size={16} className="shrink-0 text-muted" />
            <input
              type="text"
              value={busca}
              onChange={(e) => {
                setBusca(e.target.value);
                setAberto(true);
              }}
              onFocus={() => setAberto(true)}
              onKeyDown={aoTeclar}
              placeholder={placeholder}
              autoComplete="off"
              className="min-w-0 flex-1 bg-transparent text-sm text-text placeholder:text-muted focus:outline-none"
            />
            {selecionados.length > 0 && (
              <span className="shrink-0 rounded-full bg-primary px-2 py-0.5 text-[11px] font-extrabold text-on-primary">
                {selecionados.length}
              </span>
            )}
            <ChevronDown
              size={16}
              className={`shrink-0 text-muted transition-transform ${aberto ? "rotate-180" : ""}`}
            />
          </div>

          {aberto && (
            <div
              role="listbox"
              className="absolute left-0 right-0 top-[calc(100%+6px)] z-20 max-h-[268px] overflow-y-auto rounded-md border border-border bg-surface p-1.5 shadow-lg"
            >
              {listaFiltrada.length === 0 ? (
                <p className="px-3 py-5 text-center text-sm text-muted">
                  Nenhum ministério encontrado para &quot;{busca}&quot;.
                </p>
              ) : (
                listaFiltrada.map((m) => {
                  const marcado = selecionadosSet.has(m.id);
                  return (
                    <div
                      key={m.id}
                      role="option"
                      aria-selected={marcado}
                      onClick={() => alternar(m.id)}
                      className="flex cursor-pointer items-center gap-2.5 rounded px-2.5 py-2 text-sm text-text hover:bg-surface-2"
                    >
                      <span
                        className={`grid h-[18px] w-[18px] shrink-0 place-items-center rounded border ${
                          marcado ? "border-primary bg-primary" : "border-border bg-surface"
                        }`}
                      >
                        {marcado && <Check size={12} className="text-on-primary" />}
                      </span>
                      <span className="flex-1">{m.nome}</span>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
